import { isProjectCapabilityEnabled } from "./projectCapabilities";

function clean(value) {
  return String(value ?? "").trim();
}

function projectKey(project) {
  return clean(project?.slug || project?.id || project?._id);
}

function businessGoals(project) {
  const values = project?.onboarding?.businessGoals;
  return Array.isArray(values) ? values.map((value) => clean(value)).filter(Boolean) : [];
}

function pendingFact(facts, code) {
  const current = facts?.[code];
  return current?.required === true && current?.complete !== true;
}

function serviceFirst(project) {
  const goals = businessGoals(project);
  return goals.includes("offerServices")
    || (["services", "gym"].includes(clean(project?.businessProfile)) && !goals.includes("sellProducts"));
}

function usesTableReservationFlow(project) {
  if (isProjectCapabilityEnabled(project, "tableReservations")) return true;
  return clean(project?.businessProfile) === "restaurant"
    && isProjectCapabilityEnabled(project, "tables");
}

function operationalTask({ fact, base, itemPath, gastronomyPath, project, modules = {} }) {
  if (!fact || fact.required !== true || fact.complete === true) return null;

  const milestones = fact?.milestones && typeof fact.milestones === "object"
    ? fact.milestones
    : null;
  const pendingMilestone = milestones
    ? [
        "customers",
        "memberships",
        "serviceReservations",
        "gastronomicMenu",
        "tables",
        "recipes",
        "tableReservations",
        "kitchenOrders",
        "system",
      ].find((code) => milestones?.[code]?.required === true && milestones?.[code]?.complete !== true)
    : "";

  if (pendingMilestone === "customers") {
    return {
      id: "operational-customers",
      label: "Registrar el primer cliente",
      description: "Cargá un cliente real en el CRM para iniciar el seguimiento del negocio.",
      path: `${base}/clientes?accion=crear-cliente`,
      priority: 55,
    };
  }
  if (pendingMilestone === "memberships") {
    return {
      id: "operational-memberships",
      label: "Asignar la primera membresía",
      description: "Usá un cliente y un servicio o plan real para dejar funcionando el circuito de membresías.",
      path: `${base}/clientes?vista=membresias&accion=asignar-plan`,
      priority: 55,
    };
  }
  if (pendingMilestone === "serviceReservations") {
    return {
      id: "operational-service-reservations",
      label: "Preparar agenda de servicios",
      description: "Definí disponibilidad, un servicio reservable o registrá el primer turno real.",
      path: `${base}/agenda?accion=crear-turno`,
      priority: 55,
    };
  }
  if (pendingMilestone === "gastronomicMenu") {
    return {
      id: "operational-gastronomic-menu",
      label: "Preparar la carta gastronómica",
      description: "Incluí al menos un ítem real en la carta para dejar visible la oferta del restaurante.",
      path: `${itemPath}?vista=carta&crear=1&tipo=product`,
      priority: 55,
    };
  }
  if (pendingMilestone === "tables") {
    return {
      id: "operational-tables",
      label: "Configurar la primera mesa",
      description: "Creá una mesa real para habilitar la operación de salón y sus flujos asociados.",
      path: `${base}/mesas?accion=crear-mesa`,
      priority: 55,
    };
  }
  if (pendingMilestone === "recipes") {
    return {
      id: "operational-recipes",
      label: "Cargar el primer insumo",
      description: "Agregá un insumo real para que la capacidad Recetas tenga una base operativa verificable.",
      path: `${itemPath}?vista=recetas&crear=1&tipo=supply`,
      priority: 55,
    };
  }
  if (pendingMilestone === "tableReservations") {
    return {
      id: "operational-table-reservations",
      label: "Registrar la primera reserva de mesa",
      description: "Usá las mesas configuradas para comprobar el circuito real de reservas gastronómicas.",
      path: `${base}/mesas?vista=reservas&accion=crear-reserva`,
      priority: 55,
    };
  }
  if (pendingMilestone === "kitchenOrders") {
    return {
      id: "operational-kitchen-orders",
      label: "Registrar la primera comanda",
      description: "Creá una comanda real para poner en marcha el circuito de cocina.",
      path: modules?.orders ? `${base}/pedidos?vista=comandas` : gastronomyPath || itemPath,
      priority: 55,
    };
  }
  if (pendingMilestone === "system") {
    return {
      id: "operational-system",
      label: "Registrar la primera operación",
      description: "Empezá Gestión del negocio con una caja activa o un movimiento real.",
      path: `${base}/sistema/movimientos?nuevo=1`,
      priority: 55,
    };
  }

  // Compatibilidad con readiness v3 y proyectos todavía no recalculados.
  const targets = fact?.targets && typeof fact.targets === "object" ? fact.targets : {};
  const evidence = fact?.evidence && typeof fact.evidence === "object" ? fact.evidence : {};
  const pending = ["customers", "reservations", "gastronomy", "system"].find(
    (target) => targets[target] === true && evidence[target] !== true,
  );

  if (pending === "customers") {
    return {
      id: "operational-customers",
      label: "Empezar a gestionar clientes",
      description: "Registrá o gestioná el primer cliente real del negocio.",
      path: `${base}/clientes`,
      priority: 55,
    };
  }
  if (pending === "reservations") {
    const tableReservations = usesTableReservationFlow(project);
    return {
      id: "operational-reservations",
      label: tableReservations ? "Preparar reservas de mesa" : "Preparar agenda de servicios",
      description: tableReservations
        ? "Configurá mesas y el circuito gastronómico de reservas sin mezclarlo con los turnos de servicios."
        : "Configurá disponibilidad y empezá el circuito de turnos y reservas de servicios.",
      path: tableReservations ? `${base}/mesas` : `${base}/agenda`,
      priority: 55,
    };
  }
  if (pending === "gastronomy") {
    return {
      id: "operational-gastronomy",
      label: "Preparar la operación gastronómica",
      description: "Revisá los ítems de la carta y dejá una opción real lista para operar.",
      path: gastronomyPath || itemPath,
      priority: 55,
    };
  }
  if (pending === "system") {
    return {
      id: "operational-system",
      label: "Registrar la primera operación",
      description: "Empezá Gestión del negocio con un movimiento real.",
      path: `${base}/sistema/movimientos?nuevo=1`,
      priority: 55,
    };
  }

  return {
    id: "operational-management",
    label: "Revisar configuración operativa",
    description: "Entrá a Gestión del negocio y completá la preparación pendiente.",
    path: `${base}/sistema`,
    priority: 55,
  };
}

export function buildProjectActionTasks({ project, readiness, modules = {} } = {}) {
  const key = projectKey(project);
  if (!key || !readiness || typeof readiness !== "object") return [];

  const facts = readiness?.facts && typeof readiness.facts === "object" ? readiness.facts : {};
  const base = `/dashboard/proyectos/${key}`;
  const itemPath = modules?.system ? `${base}/sistema/items` : `${base}/productos`;
  const gastronomyPath = isProjectCapabilityEnabled(project, "gastronomicMenu")
    ? `${itemPath}?vista=carta`
    : isProjectCapabilityEnabled(project, "tables")
      ? `${base}/mesas?vista=mesas`
      : isProjectCapabilityEnabled(project, "tableReservations")
        ? `${base}/mesas?vista=reservas`
        : isProjectCapabilityEnabled(project, "kitchenOrders") && modules?.orders
          ? `${base}/pedidos?vista=comandas`
          : isProjectCapabilityEnabled(project, "recipes")
            ? `${itemPath}?vista=recetas`
            : itemPath;
  const firstService = serviceFirst(project);
  const tasks = [];

  const add = (condition, task) => {
    if (condition) tasks.push(task);
  };

  add(pendingFact(facts, "identity"), {
    id: "identity",
    label: "Completar datos del negocio",
    description: "Revisá la identidad básica del proyecto antes de seguir.",
    path: `${base}/configuracion/general`,
    priority: 10,
  });

  add(pendingFact(facts, "pageStarted"), {
    id: "page-started",
    label: "Empezar la página",
    description: "Dale forma a la portada y a los datos principales del negocio.",
    path: `${base}/pagina`,
    priority: 20,
  });

  add(pendingFact(facts, "contact"), {
    id: "contact",
    label: "Completar datos de contacto",
    description: "Sumá teléfono, WhatsApp, email o dirección para que puedan encontrarte.",
    path: `${base}/pagina?mision=datos`,
    priority: 30,
  });

  add(pendingFact(facts, "firstItem"), {
    id: "first-item",
    label: firstService ? "Crear el primer servicio" : "Crear el primer producto",
    description: firstService
      ? "Cargá un servicio real para mostrarlo, agendarlo o gestionarlo."
      : "Cargá un producto real con la información mínima para empezar.",
    path: `${itemPath}?crear=1&tipo=${firstService ? "service" : "product"}`,
    priority: 35,
  });

  add(pendingFact(facts, "publicContent") && !pendingFact(facts, "pageStarted"), {
    id: "public-content",
    label: "Preparar contenido público",
    description: "Agregá contenido propio para que la página no salga vacía.",
    path: `${base}/pagina`,
    priority: 40,
  });

  const operational = operationalTask({
    fact: facts.operationalManagement,
    base,
    itemPath,
    gastronomyPath,
    project,
    modules,
  });
  if (operational) tasks.push(operational);

  add(pendingFact(facts, "paymentMethod"), {
    id: "payment-method",
    label: "Configurar cobros",
    description: modules?.ecommerce
      ? "Conectá un medio de cobro online para habilitar los pagos que necesita este negocio."
      : "Revisá el acceso a cobros online para completar esta configuración.",
    path: modules?.ecommerce ? `${base}/configuracion/ecommerce` : `${base}/configuracion/suscripcion`,
    priority: 60,
  });

  const domainFact = facts.customDomain;
  add(domainFact?.configured === true && domainFact?.complete !== true, {
    id: "custom-domain",
    label: domainFact?.failedCount > 0 ? "Resolver el dominio" : "Terminar de conectar el dominio",
    description: domainFact?.failedCount > 0
      ? "La validación del dominio necesita una revisión de DNS o HTTPS."
      : "La conexión ya empezó, pero todavía falta completar DNS o HTTPS.",
    path: `${base}/configuracion/general`,
    priority: 65,
  });

  add(pendingFact(facts, "publication"), {
    id: "publication",
    label: "Publicar la página",
    description: "Revisá la versión pública y dejala disponible para compartir con clientes.",
    path: `${base}/pagina?mision=publicar`,
    priority: 70,
  });

  return tasks
    .filter((task) => task && task.path)
    .sort((a, b) => Number(a.priority || 999) - Number(b.priority || 999));
}

function roadmapMilestonePending(milestones, code) {
  const current = milestones?.[code];
  if (!current || current.complete === true) return false;
  return current.required === true || current.roadmap === true;
}

function roadmapMilestoneDoneOrAbsent(milestones, code) {
  const current = milestones?.[code];
  return !current || current.complete === true;
}

export function buildProjectRoadmap({ project, readiness, modules = {} } = {}) {
  const key = projectKey(project);
  if (!key || !readiness || typeof readiness !== "object") return [];

  const facts = readiness?.facts && typeof readiness.facts === "object" ? readiness.facts : {};
  const operationalFact = facts?.operationalManagement && typeof facts.operationalManagement === "object"
    ? facts.operationalManagement
    : {};
  const milestones = operationalFact?.milestones && typeof operationalFact.milestones === "object"
    ? operationalFact.milestones
    : {};
  const base = `/dashboard/proyectos/${key}`;
  const itemPath = modules?.system ? `${base}/sistema/items` : `${base}/productos`;
  const firstService = serviceFirst(project);
  const firstItemReady = facts?.firstItem?.required !== true || facts?.firstItem?.complete === true;
  const tasks = [];
  const add = (condition, task) => {
    if (condition && task?.path) tasks.push(task);
  };

  add(pendingFact(facts, "identity"), {
    id: "roadmap-identity",
    code: "identity",
    title: "Completar los datos del negocio",
    label: "Completar datos",
    description: "Revisá el nombre y la identidad base antes de avanzar con la puesta en marcha.",
    path: `${base}/configuracion/general`,
    color: "dark",
    priority: 5,
    operational: false,
  });

  add(pendingFact(facts, "pageStarted"), {
    id: "roadmap-page",
    code: "pageStarted",
    title: "Empezar la página",
    label: "Editar página",
    description: "Dale forma a la portada para que el negocio ya tenga una presencia propia.",
    path: `/editor-web/${encodeURIComponent(key)}`,
    color: "orange",
    priority: 10,
    operational: false,
  });

  add(pendingFact(facts, "firstItem"), {
    id: "roadmap-first-item",
    code: "firstItem",
    title: firstService ? "Crear el primer servicio" : "Crear el primer producto",
    label: firstService ? "Nuevo servicio" : "Nuevo producto",
    description: firstService
      ? "Cargá una oferta real para poder mostrarla, agendarla o gestionarla."
      : "Cargá una oferta real para empezar catálogo, pedidos, stock o gestión.",
    path: `${itemPath}?crear=1&tipo=${firstService ? "service" : "product"}`,
    color: firstService ? "blue" : "purple",
    priority: 20,
    operational: true,
  });

  add(roadmapMilestonePending(milestones, "customers"), {
    id: "roadmap-customers",
    code: "customers",
    title: "Registrar el primer cliente",
    label: "Nuevo cliente",
    description: "Creá el primer cliente real para empezar el seguimiento comercial y habilitar los flujos que dependen de él.",
    path: `${base}/clientes?accion=crear-cliente`,
    color: "purple",
    priority: 30,
    operational: true,
  });

  add(
    roadmapMilestonePending(milestones, "memberships")
      && roadmapMilestoneDoneOrAbsent(milestones, "customers"),
    {
      id: "roadmap-memberships",
      code: "memberships",
      title: "Asignar la primera membresía",
      label: "Asignar membresía",
      description: "Vinculá un cliente con un servicio o plan real y dejá funcionando el circuito de membresías.",
      path: `${base}/clientes?vista=membresias&accion=asignar-plan`,
      color: "purple",
      priority: 35,
      operational: true,
    },
  );

  add(roadmapMilestonePending(milestones, "gastronomicMenu") && firstItemReady, {
    id: "roadmap-gastronomic-menu",
    code: "gastronomicMenu",
    title: "Preparar la carta",
    label: "Abrir carta",
    description: "Incluí una oferta real en la carta para que el restaurante ya tenga qué mostrar y vender.",
    path: `${itemPath}?vista=carta&crear=1&tipo=product`,
    color: "purple",
    priority: 38,
    operational: true,
  });

  add(roadmapMilestonePending(milestones, "serviceReservations") && firstItemReady, {
    id: "roadmap-agenda",
    code: "serviceReservations",
    title: "Preparar la agenda",
    label: "Abrir agenda",
    description: "Definí disponibilidad o dejá un servicio reservable para empezar a recibir turnos reales.",
    path: `${base}/agenda?accion=crear-turno`,
    color: "blue",
    priority: 40,
    operational: true,
  });

  add(roadmapMilestonePending(milestones, "tables"), {
    id: "roadmap-tables",
    code: "tables",
    title: "Configurar la primera mesa",
    label: "Configurar mesa",
    description: "Creá una mesa real para habilitar el circuito de salón, reservas y comandas.",
    path: `${base}/mesas?accion=crear-mesa`,
    color: "orange",
    priority: 45,
    operational: true,
  });

  add(roadmapMilestonePending(milestones, "recipes") && firstItemReady, {
    id: "roadmap-recipes",
    code: "recipes",
    title: "Cargar el primer insumo",
    label: "Abrir insumos",
    description: "Sumá un insumo real para que recetas y consumo interno tengan una base operativa.",
    path: `${itemPath}?vista=recetas&crear=1&tipo=supply`,
    color: "green",
    priority: 50,
    operational: true,
  });

  add(
    roadmapMilestonePending(milestones, "tableReservations")
      && roadmapMilestoneDoneOrAbsent(milestones, "tables"),
    {
      id: "roadmap-table-reservations",
      code: "tableReservations",
      title: "Registrar la primera reserva de mesa",
      label: "Nueva reserva",
      description: "Probá el circuito real de reservas usando las mesas que ya configuraste.",
      path: `${base}/mesas?vista=reservas&accion=crear-reserva`,
      color: "orange",
      priority: 55,
      operational: true,
    },
  );

  add(roadmapMilestonePending(milestones, "orders") && firstItemReady, {
    id: "roadmap-orders",
    code: "orders",
    title: "Llegar al primer pedido",
    label: "Ver pedidos",
    description: "Con la oferta lista, usá Pedidos para seguir la primera solicitud comercial real que ingrese desde la web.",
    path: `${base}/pedidos`,
    color: "blue",
    priority: 60,
    operational: true,
  });

  add(
    roadmapMilestonePending(milestones, "kitchenOrders")
      && firstItemReady
      && roadmapMilestoneDoneOrAbsent(milestones, "tables"),
    {
      id: "roadmap-kitchen-orders",
      code: "kitchenOrders",
      title: "Registrar el primer pedido o comanda",
      label: modules?.orders ? "Abrir comandas" : "Abrir operación",
      description: "Poné en marcha el circuito gastronómico con una comanda real vinculada a la operación del salón.",
      path: modules?.orders ? `${base}/pedidos?vista=comandas` : `${base}/mesas`,
      color: "orange",
      priority: 62,
      operational: true,
    },
  );

  add(roadmapMilestonePending(milestones, "purchases") && firstItemReady, {
    id: "roadmap-purchases",
    code: "purchases",
    title: "Registrar la primera compra",
    label: "Nueva compra",
    description: "Ingresá una compra real para mover stock y dejar asentado el costo desde Gestión.",
    path: `${base}/sistema/movimientos?nuevo=1&tipo=purchase`,
    color: "green",
    priority: 65,
    operational: true,
  });

  add(
    roadmapMilestonePending(milestones, "system")
      && !roadmapMilestonePending(milestones, "purchases"),
    {
      id: "roadmap-system",
      code: "system",
      title: "Registrar la primera gestión",
      label: "Nueva operación",
      description: "Creá una caja o un movimiento real para empezar a controlar la operación desde Gestión.",
      path: `${base}/sistema/movimientos?nuevo=1`,
      color: "green",
      priority: 70,
      operational: true,
    },
  );

  // Compatibilidad con readiness anteriores: si todavía no existe el mapa de
  // hitos operativos, conservamos una única salida operativa verificable.
  if (!Object.keys(milestones).length) {
    const legacyOperational = operationalTask({
      fact: operationalFact,
      base,
      itemPath,
      gastronomyPath: itemPath,
      project,
      modules,
    });
    if (legacyOperational) {
      tasks.push({
        ...legacyOperational,
        id: `roadmap-${legacyOperational.id}`,
        code: "operationalManagement",
        title: legacyOperational.label,
        label: "Continuar",
        color: "green",
        operational: true,
      });
    }
  }

  const pageDetailsPending = !pendingFact(facts, "pageStarted")
    && (pendingFact(facts, "contact") || pendingFact(facts, "publicContent"));
  add(pageDetailsPending, {
    id: "roadmap-page-details",
    code: "pageDetails",
    title: "Completar la página",
    label: "Completar página",
    description: "Sumá contenido propio y al menos un dato de contacto para dejar una presencia pública útil.",
    path: pendingFact(facts, "contact")
      ? `${base}/pagina?mision=datos`
      : `${base}/pagina?mision=portada`,
    color: "orange",
    priority: 75,
    operational: false,
  });

  add(pendingFact(facts, "paymentMethod"), {
    id: "roadmap-payment",
    code: "paymentMethod",
    title: "Preparar los cobros",
    label: modules?.ecommerce ? "Configurar cobros" : "Ver acceso",
    description: "Conectá el medio de cobro cuando sea necesario para el recorrido elegido por este negocio.",
    path: modules?.ecommerce ? `${base}/configuracion/ecommerce` : `${base}/configuracion/suscripcion`,
    color: "green",
    priority: 80,
    operational: modules?.ecommerce === true,
  });

  add(pendingFact(facts, "publication"), {
    id: "roadmap-publication",
    code: "publication",
    title: "Publicar la página",
    label: "Revisar publicación",
    description: "Cuando la base esté lista, publicá la versión que van a ver y compartir tus clientes.",
    path: `${base}/pagina?mision=publicar&accion=publicar`,
    color: "orange",
    priority: 90,
    operational: false,
  });

  const domainFact = facts?.customDomain;
  add(domainFact?.configured === true && domainFact?.complete !== true, {
    id: "roadmap-custom-domain",
    code: "customDomain",
    title: domainFact?.failedCount > 0 ? "Resolver el dominio" : "Terminar de conectar el dominio",
    label: "Revisar dominio",
    description: domainFact?.failedCount > 0
      ? "La validación del dominio necesita una revisión de DNS o HTTPS."
      : "La conexión ya empezó; terminá DNS o HTTPS para dejarla activa.",
    path: `${base}/configuracion/general`,
    color: "dark",
    priority: 100,
    operational: false,
  });

  return tasks
    .filter((task) => task && task.path)
    .sort((a, b) => Number(a.priority || 999) - Number(b.priority || 999));
}

export function buildProjectPreparationTasks({ project, readiness, modules = {}, limit = 3 } = {}) {
  const roadmap = buildProjectRoadmap({ project, readiness, modules });
  const parsedLimit = Number(limit);
  const visibleLimit = Number.isFinite(parsedLimit) ? Math.max(1, Math.floor(parsedLimit)) : 3;

  return roadmap.slice(0, visibleLimit).map((step, index) => ({
    id: step?.id || `preparation-${step?.code || index}`,
    code: step?.code || "",
    label: step?.title || step?.label || "Continuar preparación",
    actionLabel: step?.label || "Continuar",
    description: step?.description || "",
    path: step?.path || "",
    priority: Number(step?.priority || 999),
    operational: step?.operational === true,
    source: "readiness-roadmap",
    isNext: index === 0,
  })).filter((task) => task.path);
}
